/**
 * Live-update refresh policy for list pages (A1 + A12,
 * docs/incidents/2026-09-24-sunday-fixes-validation.md §4/§5).
 *
 * The senior list used to reload its three queries on EVERY websocket event
 * (~0.65 events/s per copy at the Sunday peak), including in hidden tabs —
 * about 40 % of all API calls in the busy minutes. This policy:
 *
 *   - throttles refreshes to at most one per `intervalMs`, leading + trailing
 *     (the first event refreshes at once; events inside the window collapse
 *     into one refresh at the end of the window, so nothing is lost);
 *   - skips refreshes entirely while the tab is hidden, and does exactly one
 *     refresh when the tab becomes visible again if anything was skipped.
 *
 * `createLiveRefreshPolicy` is pure (injectable clock/timers) so it can be
 * unit-tested; `useLiveRefresh` wires it to `document.visibilityState`.
 */
import { useCallback, useEffect, useRef } from "react";

export const LIVE_REFRESH_INTERVAL_MS = 5_000;

export interface LiveRefreshDeps {
  refresh: () => void;
  isHidden: () => boolean;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
  intervalMs?: number;
}

export interface LiveRefreshPolicy {
  /** A live event arrived. */
  onEvent: () => void;
  /** The tab became visible. */
  onVisible: () => void;
  /** Cancel any pending trailing refresh. */
  dispose: () => void;
}

export function createLiveRefreshPolicy(deps: LiveRefreshDeps): LiveRefreshPolicy {
  const now = deps.now ?? (() => Date.now());
  const setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = deps.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  const intervalMs = deps.intervalMs ?? LIVE_REFRESH_INTERVAL_MS;

  let lastRefreshAt = -Infinity;
  let trailing: unknown = null;
  let skippedWhileHidden = false;

  const run = () => {
    lastRefreshAt = now();
    deps.refresh();
  };

  const onEvent = () => {
    if (deps.isHidden()) {
      skippedWhileHidden = true;
      return;
    }
    const elapsed = now() - lastRefreshAt;
    if (elapsed >= intervalMs) {
      run(); // leading edge
      return;
    }
    if (trailing == null) {
      trailing = setTimer(() => {
        trailing = null;
        if (deps.isHidden()) {
          skippedWhileHidden = true; // the window closed while hidden: defer to onVisible
          return;
        }
        run(); // trailing edge
      }, intervalMs - elapsed);
    }
  };

  const onVisible = () => {
    if (!skippedWhileHidden) return;
    skippedWhileHidden = false;
    if (trailing != null) {
      clearTimer(trailing);
      trailing = null;
    }
    run();
  };

  const dispose = () => {
    if (trailing != null) {
      clearTimer(trailing);
      trailing = null;
    }
  };

  return { onEvent, onVisible, dispose };
}

function documentHidden(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

/**
 * React binding. `refresh` may change identity between renders; the latest
 * one is always called. Returns a stable `onEvent` to hand to the websocket
 * hook.
 */
export function useLiveRefresh(refresh: () => void, intervalMs: number = LIVE_REFRESH_INTERVAL_MS): () => void {
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  const policyRef = useRef<LiveRefreshPolicy | null>(null);
  if (policyRef.current == null) {
    policyRef.current = createLiveRefreshPolicy({
      refresh: () => refreshRef.current(),
      isHidden: documentHidden,
      intervalMs,
    });
  }

  useEffect(() => {
    const policy = policyRef.current!;
    const onVisibility = () => {
      if (!documentHidden()) policy.onVisible();
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }
    return () => {
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
      policy.dispose();
    };
  }, []);

  return useCallback(() => policyRef.current!.onEvent(), []);
}
